import React, { useState, useEffect, useRef } from "react";

import './Ambience.css';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import soundwave from '../assets/image/Sound Wave.gif';
import crickets from '../assets/sound/Crickets_Forest_NightChirp.mp3';
import birds from '../assets/sound/Forest_Ambience_BirdsLeaves.mp3';
import frogs from '../assets/sound/Frogs_Forest_TreeFrogs.mp3';
import rain from '../assets/sound/Rain_LightRainOnTarp_Campsite.mp3';
import river from '../assets/sound/River_Forect_SoftCurrent.mp3';
import tent from '../assets/sound/Tent_Woods_RustleOnly.mp3';
import thunder from '../assets/sound/Thunder_FarAway.mp3';
import wind from '../assets/sound/Wind_Highlands_DryGrass.mp3';
import beneathTheCanopy from '../assets/music/Beneath the Canopy.mp3';
import cozyEmbers from '../assets/music/Cozy Embers.mp3';
import cozyWhispersUnderStars from '../assets/music/Cozy Whispers Under Stars.mp3';
import dreamInWaves from '../assets/music/Dream in Waves.mp3';
import flickerOfTheFire from '../assets/music/Flicker of the Fire.mp3';
import lanternGlow from '../assets/music/Lantern Glow.mp3';
import starglowRhythm from '../assets/music/Starglow Rhythm.mp3';
import velvetNightfall from '../assets/music/Velvet Nightfall.mp3';
import whisperingPines from '../assets/music/Whispering Pines.mp3';
import whisperingTreetops from '../assets/music/Whispering Treetops.mp3';
import whispersInTheWind from '../assets/music/Whispers in the Wind.mp3';
import soundIcon1 from '../assets/ui/soundIcon (1).png';
import soundIcon2 from '../assets/ui/soundIcon (2).png';
import soundIcon3 from '../assets/ui/soundIcon (3).png';
import soundIcon4 from '../assets/ui/soundIcon (4).png';
import soundIcon5 from '../assets/ui/soundIcon (5).png';
import soundIcon6 from '../assets/ui/soundIcon (6).png';
import soundIcon7 from '../assets/ui/soundIcon (7).png';
import soundIcon8 from '../assets/ui/soundIcon (8).png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBackward,
  faPlay,
  faPause,
  faForward,
  faRepeat,
  faVolumeUp,
} from '@fortawesome/free-solid-svg-icons';

const sounds = [
  { id: "crickets", label: "Crickets", file: crickets, image: soundIcon7 },
  { id: "birds", label: "Birds & Leaves", file: birds, image: soundIcon3 },
  { id: "frogs", label: "Tree Frogs", file: frogs, image: soundIcon5 },
  { id: "rain", label: "Rain on Tarp", file: rain, image: soundIcon6 },
  { id: "river", label: "River Flow", file: river, image: soundIcon4 },
  { id: "tent", label: "Tent Rustle", file: tent, image: soundIcon2 },
  { id: "thunder", label: "Distant Thunder", file: thunder, image: soundIcon8 },
  { id: "wind", label: "Dry Grass Wind", file: wind, image: soundIcon1 },
];

const musicTracks = [
  { title: "Beneath the Canopy", file: beneathTheCanopy },
  { title: "Cozy Embers", file: cozyEmbers },
  { title: "Cozy Whispers Under Stars", file: cozyWhispersUnderStars },
  { title: "Dream in Waves", file: dreamInWaves },
  { title: "Flicker of the Fire", file: flickerOfTheFire },
  { title: "Lantern Glow", file: lanternGlow },
  { title: "Starglow Rhythm", file: starglowRhythm },
  { title: "Velvet Nightfall", file: velvetNightfall },
  { title: "Whispering Pines", file: whisperingPines },
  { title: "Whispering Treetops", file: whisperingTreetops },
  { title: "Whispers in the Wind", file: whispersInTheWind },
];

export default function Ambience() {
  const [volumes, setVolumes] = useState(
    sounds.reduce((acc, sound) => {
      acc[sound.id] = 0.5;
      return acc;
    }, {})
  );

  const [ambientOpen, setAmbientOpen] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [trackIndex, setTrackIndex] = useState(0);
  const [musicVolume, setMusicVolume] = useState(0.7);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [playingSounds, setPlayingSounds] = useState({});

  const musicAudio = useRef(new Audio(musicTracks[0].file));
  const audioRefs = useRef({});
  const intervalRefs = useRef({});

  useEffect(() => {
    sounds.forEach((sound) => {
      const audio = new Audio(sound.file);
      audio.loop = true;
      audio.volume = volumes[sound.id];
      audioRefs.current[sound.id] = audio;
    });
  }, []);  

  useEffect(() => {
    Object.entries(volumes).forEach(([id, volume]) => {
      const audio = audioRefs.current[id];
      if (audio) {
        audio.volume = volume;
      }
    });
  }, [volumes]);

  useEffect(() => {
    return () => {
      Object.values(intervalRefs.current).forEach(clearInterval);
      musicAudio.current.pause();
    };
  }, []);

  useEffect(() => {
    if (isPlaying) {
      musicAudio.current.play();
    } else {
      musicAudio.current.pause();
    }
  }, [isPlaying, loop]);

  useEffect(() => {
    musicAudio.current.volume = musicVolume;
  }, [musicVolume]);

  useEffect(() => {
    const audio = musicAudio.current;
  
    const handleEnded = () => {
      if (!loop) {
        handleNextTrack();
      } else {
        audio.currentTime = 0;
        audio.play(); 
      }
    };
  
    audio.addEventListener("ended", handleEnded);
  
    return () => {
      audio.removeEventListener("ended", handleEnded);
    };
  }, [trackIndex, loop, isPlaying]);
  
  const handleSetTrack = (index) => {
    const wasPlaying = isPlaying;
  
    musicAudio.current.pause();
    musicAudio.current = new Audio(musicTracks[index].file);
    musicAudio.current.loop = loop;
    musicAudio.current.volume = musicVolume;
  
    if (wasPlaying) musicAudio.current.play();
    setTrackIndex(index);
  };
  
  const handleNextTrack = () => {
    const next = (trackIndex + 1) % musicTracks.length;
    handleSetTrack(next);
  };
  
  const handlePrevTrack = () => {
    const prev = (trackIndex - 1 + musicTracks.length) % musicTracks.length;
    handleSetTrack(prev);
  };  

  const handleVolumeChange = (id, value) => {
    setVolumes((prev) => ({ ...prev, [id]: value }));
  };

  const handlePlayToggle = (id) => {
    const audio = audioRefs.current[id];
  
    const updatePlayingState = (playing) => {
      setPlayingSounds((prev) => ({ ...prev, [id]: playing }));
    };
  
    if (audio) {
      if (audio.paused) {
        audio.play();
        updatePlayingState(true);
      } else {
        audio.pause();
        audio.currentTime = 0;
        updatePlayingState(false);
      }
    }
  };
  
  return (
    <div className="lofi ambience-container">
      {!ambientOpen && (
        <div className="ambient-restore-wrapper">
          <div className="tooltip-container">
            <button className="ambient-restore" onClick={() => setAmbientOpen(true)}>
              <img src={soundwave} alt="Open Panel" className="ambient-wave-icon" />
            </button>
            <div className="tooltip-text">Open Ambient Mixer Panel</div>
          </div>
        </div>
      )}

      {ambientOpen && (
        <div className="ambient-panel frosted">
          <div className="ambient-collapse-wrapper">
            <div className="tooltip-container">
              <button className="ambient-collapse" onClick={() => setAmbientOpen(false)}>−</button>
              <div className="tooltip-text">Minimize window</div>
            </div>
          </div>

          <div className="sound-grid">
            {sounds.map((sound) => (
            <div key={sound.id} className="sound-control">
              <div
                className={`sound-label-wrapper ${playingSounds[sound.id] ? 'active' : ''}`}
                onClick={() => handlePlayToggle(sound.id)}
              >
                <div className="sound-icon">
                  <img src={sound.image} alt={sound.label} className="sound-icon-img" />
                </div>
                <div className="sound-label">{sound.label}</div>
              </div>

              <Slider
                className={`${playingSounds[sound.id] ? 'visible' : 'hidden'}`}
                min={0}
                max={1}
                step={0.01}
                value={volumes[sound.id]}
                onChange={(value) => handleVolumeChange(sound.id, value)}
                trackStyle={{ backgroundColor: 'white', height: 4 }}
                handleStyle={{
                  borderColor: 'white',
                  height: 12,
                  width: 12,
                  marginTop: -4,
                  backgroundColor: 'white',
                }}
                railStyle={{ backgroundColor: '#999', height: 4 }}
              />

            </div>

            ))}
          </div>
        </div>
      )}

      <div className="music-player frosted">
        <button onClick={handlePrevTrack}>
          <FontAwesomeIcon icon={faBackward} />
        </button>

        <button onClick={() => setIsPlaying(!isPlaying)}>
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        </button>

        <button onClick={handleNextTrack}>
          <FontAwesomeIcon icon={faForward} />
        </button>

        <button onClick={() => setLoop(!loop)} style={{ opacity: loop ? 1 : 0.5 }}>
          <FontAwesomeIcon icon={faRepeat} />
        </button>

        <button className="volume-button" onClick={() => setShowVolumeSlider(!showVolumeSlider)}>
          {showVolumeSlider && (
            <div className="volume-slider">
              <Slider
                vertical
                min={0}
                max={1}
                step={0.01}
                value={musicVolume}
                onChange={(value) => setMusicVolume(value)}
                trackStyle={{ backgroundColor: 'white', width: 4 }}
                handleStyle={{
                  borderColor: 'white',
                  height: 12,
                  width: 12,
                  marginLeft: -4,
                  backgroundColor: 'white',
                }}
                railStyle={{ backgroundColor: '#999', width: 4 }}
              />
            </div>
          )}
          <FontAwesomeIcon icon={faVolumeUp} />
        </button>

        <div className="track-title-wrapper">
          <div className="track-title-slide">
            {musicTracks[trackIndex].title}
          </div>
        </div>
      </div>

      <div className="music-footer">
        <a
          href="https://embedbyjackie.gumroad.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Embed by Jackie
        </a>
        <span>☕</span>
        <a
          href="https://buymeacoffee.com/jdrizzle"
          target="_blank"
          rel="noopener noreferrer"
        >
          Buy me a coffee
        </a>
      </div>
      

    </div>
  );
}